import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import PreviewRenderer from '../components/PreviewRenderer';
import { AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ArticlePreviewPage
 * Renders a single article via `/api/public/preview-article/<token>`.
 * Used as the iframe target for the in-editor mobile/tablet preview
 * switcher (ArticleEditor). Has no authentication; the token itself is
 * the credential.
 */
const ArticlePreviewPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const resp = await axios.get(`${API_URL}/api/public/preview-article/${token}`);
        if (!cancelled) {
          setData(resp.data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.detail || 'Preview not found or link has expired');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mr-2" />
        Loading preview…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md text-center">
          <AlertTriangle className="mx-auto mb-3 text-amber-500" size={36} />
          <p className="text-slate-700">{error}</p>
        </div>
      </div>
    );
  }

  if (!data?.article) return null;

  const article = data.article;
  const theme = data.theme || {};

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto py-8 px-4">
        {/* Hero image */}
        {article.image_url && (
          <img
            src={article.image_url}
            alt={article.image_alt || article.title}
            className={`w-full h-64 md:h-96 rounded-lg mb-6 ${article.image_fit === 'contain' ? 'object-contain bg-slate-100' : 'object-cover'}`}
          />
        )}
        {/* Title */}
        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{article.title}</h1>
        {/* Meta */}
        {article.show_date && article.date && (
          <p className="text-slate-500 mb-6">
            {new Date(article.date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
            {(article.categories || []).length > 0 && (
              <span className="ml-3">
                {(article.categories || []).map((cat) => (
                  <span key={cat} className="px-2 py-1 bg-slate-100 rounded text-sm mr-1">{cat}</span>
                ))}
              </span>
            )}
          </p>
        )}
        {/* Body — blocks first, fallback to content_html */}
        {article.blocks && article.blocks.length > 0 ? (
          <PreviewRenderer
            blocks={article.blocks}
            siteTheme={theme}
            isQuickPreview={false}
            showNavigation={false}
          />
        ) : article.content_html ? (
          <div className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: article.content_html }} />
        ) : (
          <p className="text-slate-400 italic">No content yet</p>
        )}
      </div>
    </div>
  );
};

export default ArticlePreviewPage;
